import React, { useState, useEffect } from 'react';
import { getColumn } from '../../utils/fn';
import './LineItemsGrid.css';

const LineItemsGrid = ({ lineItems = [], onChange, readOnly = false, variant = 'default' }) => {
  const isDebitNote = variant === 'debit-note';
  const isCreditNote = variant === 'credit-note';
  const [items, setItems] = useState(lineItems);
  const [unitOptions, setUnitOptions] = useState([]);
  const [descriptionOptions, setDescriptionOptions] = useState([]);

  useEffect(() => {
    setItems(lineItems);
  }, [lineItems]);

  useEffect(() => {
    if (isCreditNote) return;

    const fetchUnits = async () => {
      try {
        const units = await getColumn("common_master", "unit");
        setUnitOptions(units || []);
      } catch (error) {
        console.error("Error fetching units:", error);
        setUnitOptions([
          { id: "NOS", value: "NOS" },
          { id: "DAYS", value: "DAYS" },
          { id: "HOURS", value: "HOURS" },
          { id: "LTRS", value: "LTRS" },
          { id: "KGS", value: "KGS" },
          { id: "MTR", value: "MTR" },
          { id: "SQM", value: "SQM" },
          { id: "TRIPS", value: "TRIPS" },
        ]);
      }
    };
    fetchUnits();
  }, [isCreditNote]);

  useEffect(() => {
    if (!isDebitNote) return;

    const fetchDescriptions = async () => {
      try {
        const [descriptions, hsnCodes] = await Promise.all([
          getColumn("common_master", "debitnote_descriptions"),
          getColumn("common_master", "debitnote_hsn_code"),
        ]);

        const hsnById = (hsnCodes || []).reduce((acc, row) => {
          acc[row.id] = row.value;
          return acc;
        }, {});

        const merged = (descriptions || []).map((row) => ({
          ...row,
          hsn_code: hsnById[row.id] || '',
        }));

        setDescriptionOptions(merged);
      } catch (error) {
        console.error("Error fetching debit note descriptions:", error);
        setDescriptionOptions([]);
      }
    };
    fetchDescriptions();
  }, [isDebitNote]);

  const addRow = () => {
    const newItem = {
      line_no: items.length + 1,
      description: '',
      remarks: '',
      hsn_code: '',
      quantity: 1,
      unit: 'NOS',
      rate: 0,
      amount: 0,
    };
    const updated = [...items, newItem];
    setItems(updated);
    onChange(updated);
  };

  const deleteRow = (index) => {
    const updated = items.filter((_, i) => i !== index);
    updated.forEach((item, i) => {
      item.line_no = i + 1;
    });
    setItems(updated);
    onChange(updated);
  };

  const updateRow = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;

    if (field === 'description' && isDebitNote) {
      const selected = descriptionOptions.find((opt) => opt.value === value);
      if (selected?.hsn_code) {
        updated[index].hsn_code = selected.hsn_code;
      }
    }

    if (field === 'quantity' || field === 'rate') {
      const qty = parseFloat(updated[index].quantity) || 0;
      const rate = parseFloat(updated[index].rate) || 0;
      updated[index].amount = (qty * rate).toFixed(2);
    }

    setItems(updated);
    onChange(updated);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2);
  };

  const showActions = !readOnly && !isCreditNote;
  const showAddButton = !readOnly && !isCreditNote;

  const colSpanEmpty = readOnly
    ? (isDebitNote ? 8 : 7)
    : isCreditNote
      ? 7
      : (isDebitNote ? 9 : 8);

  const totalColSpan = isDebitNote ? 7 : 6;

  const isFieldReadOnly = (field) => {
    if (readOnly) return true;
    if (!isCreditNote) return false;
    return !['quantity', 'rate'].includes(field);
  };

  return (
    <div className="line-items-grid">
      <div className="line-items-header">
        <h3>Line Items</h3>
        {showAddButton && (
          <button type="button" className="btn-add-line" onClick={addRow}>
            + Add Line Item
          </button>
        )}
        {isCreditNote && !readOnly && (
          <small style={{ color: '#666' }}>
            Line items are loaded from the selected invoice. Quantity and rate are editable.
          </small>
        )}
      </div>

      <div className="line-items-table-wrapper">
        <table className="line-items-table">
          <thead>
            <tr>
              <th style={{ width: '60px' }}>Line No</th>
              <th style={{ width: isDebitNote ? '220px' : '300px' }}>Description</th>
              {isDebitNote && <th style={{ width: '180px' }}>Remarks</th>}
              <th style={{ width: '120px' }}>HSN Code</th>
              <th style={{ width: '100px' }}>Quantity</th>
              <th style={{ width: '100px' }}>Unit</th>
              <th style={{ width: '120px' }}>Rate</th>
              <th style={{ width: '120px' }}>Amount</th>
              {showActions && <th style={{ width: '80px' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={colSpanEmpty} className="no-data">
                  {readOnly
                    ? 'No line items'
                    : isCreditNote
                      ? 'Select an original invoice to load line items'
                      : 'Click "Add Line Item" to add rows'}
                </td>
              </tr>
            ) : (
              items.map((item, index) => (
                <tr key={index}>
                  <td className="text-center">{item.line_no}</td>
                  <td>
                    {isFieldReadOnly('description') ? (
                      <span>{item.description}</span>
                    ) : isDebitNote ? (
                      <select
                        className="form-control"
                        value={item.description || ''}
                        onChange={(e) => updateRow(index, 'description', e.target.value)}
                        required
                      >
                        <option value="">Select Description</option>
                        {descriptionOptions.map((opt) => (
                          <option key={opt.id} value={opt.value}>
                            {opt.value}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        value={item.description || ''}
                        onChange={(e) => updateRow(index, 'description', e.target.value)}
                        placeholder="Enter description"
                      />
                    )}
                  </td>
                  {isDebitNote && (
                    <td>
                      {isFieldReadOnly('remarks') ? (
                        <span>{item.remarks || '-'}</span>
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          value={item.remarks || ''}
                          onChange={(e) => updateRow(index, 'remarks', e.target.value)}
                          placeholder="Remarks"
                        />
                      )}
                    </td>
                  )}
                  <td>
                    {isFieldReadOnly('hsn_code') ? (
                      <span>{item.hsn_code || '-'}</span>
                    ) : (
                      <input
                        type="text"
                        className="form-control"
                        value={item.hsn_code || ''}
                        onChange={(e) => updateRow(index, 'hsn_code', e.target.value)}
                        placeholder="HSN Code"
                      />
                    )}
                  </td>
                  <td>
                    {isFieldReadOnly('quantity') ? (
                      <span className="text-right">{item.quantity}</span>
                    ) : (
                      <input
                        type="number"
                        className="form-control text-right"
                        value={item.quantity || ''}
                        onChange={(e) => updateRow(index, 'quantity', e.target.value)}
                        min={isCreditNote ? undefined : "0"}
                        step="0.01"
                      />
                    )}
                  </td>
                  <td>
                    {isFieldReadOnly('unit') ? (
                      <span>{item.unit || '-'}</span>
                    ) : (
                      <select
                        className="form-control"
                        value={item.unit || 'NOS'}
                        onChange={(e) => updateRow(index, 'unit', e.target.value)}
                      >
                        {unitOptions.length > 0 ? (
                          unitOptions.map((unit) => (
                            <option key={unit.id} value={unit.id}>
                              {unit.value}
                            </option>
                          ))
                        ) : (
                          <option value="NOS">NOS</option>
                        )}
                      </select>
                    )}
                  </td>
                  <td>
                    {isFieldReadOnly('rate') ? (
                      <span className="text-right">{parseFloat(item.rate || 0).toFixed(2)}</span>
                    ) : (
                      <input
                        type="number"
                        className="form-control text-right"
                        value={item.rate || ''}
                        onChange={(e) => updateRow(index, 'rate', e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    )}
                  </td>
                  <td className="text-right amount-cell">
                    {parseFloat(item.amount || 0).toFixed(2)}
                  </td>
                  {showActions && (
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn-delete-line"
                        onClick={() => deleteRow(index)}
                        title="Delete row"
                      >
                        🗑️
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={totalColSpan} className="text-right">
                  <strong>Total:</strong>
                </td>
                <td className="text-right">
                  <strong>₹ {calculateTotal()}</strong>
                </td>
                {showActions && <td></td>}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default LineItemsGrid;
